import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  Tabs,
  Tab,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  GetApp as ExportIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import SummaryCards from './SummaryCards';
import { getRateForRole, getProjectYear } from '../../utils/rateUtils';

// Define efficiency types
const EFFICIENCY_TYPES = {
  PROCESS_IMPROVEMENT: 'Process Improvement',
  ERROR_REDUCTION: 'Error Reduction',
  TIME_SAVINGS: 'Time Savings',
  AUTOMATION_SAVINGS: 'Automation Savings'
};

// Default CA salary for team members (annual)
const DEFAULT_CA_SALARY = 163000;

// Function to prepare stacked bar chart data
const prepareStackedBarChartData = (futureCosts, totalDevelopmentCost, years = 5, paybackPeriodMonths = null) => {
  if (!futureCosts || !Array.isArray(futureCosts) || futureCosts.length === 0) {
    return Array.from({ length: years }, (_, i) => ({
      year: `Year ${i + 1}`,
      devCost: i === 0 ? -Math.round(totalDevelopmentCost || 0) : 0,
      netValue: i === 0 ? -Math.round(totalDevelopmentCost || 0) : -Math.round(totalDevelopmentCost || 0)
    }));
  }
  
  const chartData = Array.from({ length: years }, (_, i) => ({
    year: `Year ${i + 1}`,
    devCost: i === 0 ? -Math.round(totalDevelopmentCost || 0) : 0,
    netValue: 0,
    isBreakEven: false
  }));
  
  try {
    futureCosts.forEach(teamCost => {
      if (teamCost && teamCost.yearlyData && Array.isArray(teamCost.yearlyData)) {
        teamCost.yearlyData.forEach((yearData, yearIndex) => {
          if (yearIndex < years && yearData) {
            const teamSavingsKey = `${teamCost.team} Savings`;
            chartData[yearIndex][teamSavingsKey] = Math.round(yearData.savings || 0);
          }
        });
      }
    });
    
    let cumulativeValue = -Math.round(totalDevelopmentCost || 0); // Start with negative development cost
    let breakEvenFound = false;
    
    chartData.forEach((yearData, yearIndex) => {
      // Calculate yearly savings (sum of all team savings)
      const yearlySavings = Object.entries(yearData)
        .filter(([key]) => key.endsWith(' Savings'))
        .reduce((sum, [_, value]) => sum + (value || 0), 0);
      
      // Add yearly savings to cumulative value
      cumulativeValue += yearlySavings;
      yearData.netValue = Math.round(cumulativeValue);
      
      // Check if this is the breakeven year
      if (!breakEvenFound && cumulativeValue >= 0) {
        yearData.isBreakEven = true;
        breakEvenFound = true;
      }
      
      // If we have a specific payback period in months, calculate more precise breakeven point
      if (paybackPeriodMonths && !breakEvenFound) {
        const paybackYears = paybackPeriodMonths / 12;
        if (yearIndex < paybackYears && yearIndex + 1 > paybackYears) {
          yearData.isBreakEven = true;
          breakEvenFound = true;
        }
      }
    });
    
    return chartData;
  } catch (error) {
    console.error("Error preparing chart data:", error);
    return Array.from({ length: years }, (_, i) => ({
      year: `Year ${i + 1}`,
      devCost: i === 0 ? -Math.round(totalDevelopmentCost || 0) : 0,
      netValue: i === 0 ? -Math.round(totalDevelopmentCost || 0) : -Math.round(totalDevelopmentCost || 0),
      isBreakEven: false
    }));
  }
};

function FinancialModelStep({ 
  outcomeId, 
  outcomeName, 
  data, 
  onSave, // Use onSave for explicit saves
  costEstimationData, 
  staffingData,
  outcomeData, // Add outcomeData prop to get milestones and status
  buttonStyle, 
  secondaryButtonStyle, 
  refreshData,
  autoSave = false,
  hideButtons = false,
  readOnly = false
}) {
  const theme = useTheme();
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedData, setLastSavedData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Cost source toggle state
  const [costSource, setCostSource] = useState(() => {
    // Default to 'planned' unless outcome is funded
    const status = outcomeData?.status || data?.status || 'To Do';
    return status === 'Funded' ? 'staffed' : 'planned';
  });

  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamForm, setTeamForm] = useState({
    title: '',
    count: 1,
    avg_salary: DEFAULT_CA_SALARY,
    time_percentage: 100
  });

  const [efficiencyDialogOpen, setEfficiencyDialogOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [efficiencyValue, setEfficiencyValue] = useState(0);
  const [selectedEfficiencyType, setSelectedEfficiencyType] = useState(EFFICIENCY_TYPES.PROCESS_IMPROVEMENT);
  const [savingEfficiency, setSavingEfficiency] = useState(false);
  const [efficiencySaveSuccess, setEfficiencySaveSuccess] = useState(false);

  // Button style for better visibility in dark mode
  const localButtonStyle = buttonStyle || {
    backgroundColor: theme.palette.mode === 'dark' ? '#4db6ac' : undefined,
    color: theme.palette.mode === 'dark' ? theme.palette.common.black : undefined,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#00897b' : undefined,
    }
  };

  // Helper function to calculate milestone cost from cost estimation
  const calculateMilestoneCostFromEstimation = useCallback((milestone) => {
    if (!costEstimationData?.roles || !costEstimationData?.start_month) return 0;
    
    const projectStart = dayjs(costEstimationData.start_month + '-01');
    const milestoneStart = dayjs(milestone.start_date);
    const milestoneEnd = dayjs(milestone.end_date);
    
    // Calculate which project months this milestone covers
    const milestoneStartMonth = Math.max(1, milestoneStart.diff(projectStart, 'month') + 1);
    const milestoneEndMonth = Math.min(
      costEstimationData.adjustedMonths || 6, 
      milestoneEnd.diff(projectStart, 'month') + 1
    );
    
    if (milestoneStartMonth > milestoneEndMonth) return 0;
    
    let totalCost = 0;
    
    // Calculate cost from roles
    costEstimationData.roles.forEach(role => {
      const effectiveRanges = role.dateRanges || [];
      
      effectiveRanges.forEach(range => {
        const roleStart = range.startMonth;
        const roleEnd = range.endMonth;
        const roleAllocation = (range.allocation || 0) / 100;
        
        // Find overlap between role timeline and milestone timeline
        const overlapStart = Math.max(roleStart, milestoneStartMonth);
        const overlapEnd = Math.min(roleEnd, milestoneEndMonth);
        
        if (overlapStart <= overlapEnd) {
          const overlapMonths = overlapEnd - overlapStart + 1;
          const roleCost = (role.rate || 0) * roleAllocation * overlapMonths;
          totalCost += roleCost;
        }
      });
    });
    
    // Add proportional fixed costs
    if (costEstimationData.fixedCosts) {
      costEstimationData.fixedCosts.forEach(fixedCost => {
        const costStart = fixedCost.startMonth || 1;
        const costEnd = fixedCost.endMonth || costEstimationData.adjustedMonths;
        
        // Find overlap between fixed cost timeline and milestone timeline
        const overlapStart = Math.max(costStart, milestoneStartMonth);
        const overlapEnd = Math.min(costEnd, milestoneEndMonth);
        
        if (overlapStart <= overlapEnd) {
          const overlapMonths = overlapEnd - overlapStart + 1;
          const monthlyCost = fixedCost.monthlyCost || 0;
          totalCost += monthlyCost * overlapMonths;
        }
      });
    }
    
    return Math.round(totalCost);
  }, [costEstimationData]);

    // Helper function to calculate milestone cost from staffing data with COLA adjustments
  const calculateMilestoneCostFromStaffing = useCallback(async (milestone) => {
    if (!staffingData?.milestones) return 0;
    
    const staffingMilestone = staffingData.milestones.find(m => m.id === milestone.id);
    if (!staffingMilestone?.roles) return 0;
    
    // Apply COLA adjustments if needed before calculating costs
    const projectYear = getProjectYear(outcomeData, costEstimationData, new Date().getFullYear());
    
    let adjustedRoles = staffingMilestone.roles;
    const needsUpdate = adjustedRoles.some(role => 
      !role.roster_member_id && 
      !role.rateAdjustmentInfo && 
      role.company && role.location &&
      role.rate > 0
    );
    
    if (needsUpdate) {
      console.log('FinancialModelStep: Applying COLA adjustments for milestone cost calculation, project year:', projectYear);
      
      try {
        adjustedRoles = await Promise.all(
          adjustedRoles.map(async role => {
            if (!role.roster_member_id && !role.rateAdjustmentInfo && role.company && role.location && role.rate > 0) {
              try {
                const rateInfo = await getRateForRole(role.company, role.location, role.role || '', projectYear);
                if (rateInfo.monthlyRate > 0) {
                  const isAdjusted = rateInfo.isAdjusted || Math.abs(rateInfo.monthlyRate - role.rate) > 1;
                  if (isAdjusted) {
                    console.log(`FinancialModelStep: COLA adjustment for milestone cost calculation: ${role.rate} -> ${rateInfo.monthlyRate}`);
                    return {
                      ...role,
                      rate: rateInfo.monthlyRate,
                      rateAdjustmentInfo: rateInfo.adjustmentInfo
                    };
                  }
                }
              } catch (error) {
                console.warn(`Could not get COLA rate for milestone cost calculation:`, error);
              }
            }
            return role;
          })
        );
      } catch (error) {
        console.warn('Error applying COLA adjustments to milestone cost calculation:', error);
      }
    }
    
    let totalCost = 0;
    
    // Calculate cost from staffed roles (using COLA-adjusted rates)
    adjustedRoles.forEach(role => {
      const monthlyRate = role.rate || 0;
      const allocation = (role.allocation || 0) / 100;
      const months = role.overlap_months || 1;
      totalCost += monthlyRate * allocation * months;
    });
    
    // Add proportional fixed costs from staffing data
    if (staffingData.fixedCosts) {
      const projectStart = dayjs(staffingData.start_month + '-01');
      const milestoneStart = dayjs(milestone.start_date);
      const milestoneEnd = dayjs(milestone.end_date);
      
      const milestoneStartMonth = Math.max(1, milestoneStart.diff(projectStart, 'month') + 1);
      const milestoneEndMonth = Math.min(
        staffingData.adjustedMonths || 6, 
        milestoneEnd.diff(projectStart, 'month') + 1
      );
      
      staffingData.fixedCosts.forEach(fixedCost => {
        const costStart = fixedCost.startMonth || 1;
        const costEnd = fixedCost.endMonth || staffingData.adjustedMonths;
        
        // Find overlap between fixed cost timeline and milestone timeline
        const overlapStart = Math.max(costStart, milestoneStartMonth);
        const overlapEnd = Math.min(costEnd, milestoneEndMonth);
        
        if (overlapStart <= overlapEnd) {
          const overlapMonths = overlapEnd - overlapStart + 1;
          const monthlyCost = fixedCost.monthlyCost || 0;
          totalCost += monthlyCost * overlapMonths;
        }
      });
    }
    
    return Math.round(totalCost);
  }, [staffingData, outcomeData, costEstimationData]);

    // Helper function to get milestone cost based on current cost source
  const getMilestoneCost = useCallback(async (milestone) => {
    if (costSource === 'staffed') {
      return await calculateMilestoneCostFromStaffing(milestone);
    } else {
      return calculateMilestoneCostFromEstimation(milestone);
    }
  }, [costSource, calculateMilestoneCostFromStaffing, calculateMilestoneCostFromEstimation]);

    // Helper function to sync financial milestones with outcome data
  const syncFinancialMilestonesWithOutcome = useCallback(async (currentFinancialData, outcomeMilestones) => {
    if (!outcomeMilestones || outcomeMilestones.length === 0) {
      return currentFinancialData;
    }

    const existingMilestones = currentFinancialData.milestones || [];
    const existingMilestoneMap = new Map(existingMilestones.map(m => [m.id, m]));
    
          // Sync all outcome milestones, preserving efficiency data where it exists
      const syncedMilestones = await Promise.all(
        outcomeMilestones.map(async outcomeMilestone => {
          const existingMilestone = existingMilestoneMap.get(outcomeMilestone.id);
          
          // Calculate cost asynchronously if getMilestoneCost is available
          let cost = outcomeMilestone.cost || 0;
          if (getMilestoneCost) {
            try {
              cost = await getMilestoneCost(outcomeMilestone);
            } catch (error) {
              console.warn('Error calculating milestone cost:', error);
              cost = outcomeMilestone.cost || 0;
            }
          }
          
          return {
            ...outcomeMilestone,
            cost,
            // Preserve existing efficiency data or initialize
            efficiency_gains: existingMilestone?.efficiency_gains || {},
            efficiency_types: existingMilestone?.efficiency_types || {},
          };
        })
      );

    console.log('FinancialModelStep: Syncing milestones with outcome data');
    console.log('Outcome milestones:', outcomeMilestones.map(m => ({ id: m.id, name: m.name })));
    console.log('Synced financial milestones:', syncedMilestones.map(m => ({ 
      id: m.id, 
      name: m.name, 
      cost: m.cost,
      hasEfficiencyData: Object.keys(m.efficiency_gains || {}).length > 0
    })));

    return {
      ...currentFinancialData,
      milestones: syncedMilestones
    };
  }, [getMilestoneCost]);

  const [financialData, setFinancialData] = useState(() => {
    const initialData = {
      ...data,
      teams: data?.teams || [],
      // ALWAYS prioritize outcomeData milestones over data milestones
      milestones: outcomeData?.milestones || data?.milestones || [],
      efficiency_assumptions: data?.efficiency_assumptions || {
        [EFFICIENCY_TYPES.AUTOMATION_SAVINGS]: 20,
        [EFFICIENCY_TYPES.PROCESS_IMPROVEMENT]: 15,
        [EFFICIENCY_TYPES.ERROR_REDUCTION]: 10,
        [EFFICIENCY_TYPES.TIME_SAVINGS]: 25
      },
      total_development_cost: Math.round(costEstimationData?.summary?.totalCost || costEstimationData?.totalCost || 0),
      annual_cola_percentage: data?.annual_cola_percentage || 4.5,
      projection_years: data?.projection_years || 5,
      cost_source: costSource // Track the cost source in the data
    };
    
    // Ensure milestones have efficiency data structures
    if (initialData.milestones) {
      initialData.milestones = initialData.milestones.map(milestone => ({
        ...milestone,
        efficiency_gains: milestone.efficiency_gains || {},
        efficiency_types: milestone.efficiency_types || {},
                // Cost will be updated asynchronously by useEffect
        cost: milestone.cost || 0
      }));
    }
    return initialData;
  });

  // Update cost source when outcome status changes
  useEffect(() => {
    const status = outcomeData?.status || data?.status || 'To Do';
    const newCostSource = status === 'Funded' ? 'staffed' : 'planned';
    
    if (newCostSource !== costSource) {
      setCostSource(newCostSource);
    }
  }, [outcomeData?.status, data?.status, costSource]);

  // Helper function to check if data has meaningfully changed
  const hasSignificantChanges = (newData, oldData) => {
    if (!oldData) return true;
    
    // Check for significant changes that warrant saving
    const significantFields = [
      'teams', 'milestones', 'efficiency_assumptions', 'total_development_cost',
      'annual_cola_percentage', 'projection_years', 'cost_source'
    ];
    
    return significantFields.some(field => {
      const newValue = JSON.stringify(newData[field]);
      const oldValue = JSON.stringify(oldData[field]);
      const hasChanged = newValue !== oldValue;
      
      if (hasChanged && field === 'milestones') {
        console.log('FinancialModelStep: Milestones changed:');
        console.log('Old:', oldData[field]);
        console.log('New:', newData[field]);
      }
      
      return hasChanged;
    });
  };

  // Core save function
  const saveData = async (dataToSave) => {
    if (readOnly || !onSave) {
      return;
    }
    
    // Don't save if no significant changes
    if (!hasSignificantChanges(dataToSave, lastSavedData)) {
      console.log('FinancialModelStep: Skipping save - no significant changes');
      return;
    }
    
    try {
      console.log('FinancialModelStep: Saving data:', dataToSave);
      await onSave(dataToSave);
      setLastSavedData(JSON.parse(JSON.stringify(dataToSave))); // Deep copy for tracking
      console.log('FinancialModelStep: Successfully saved data');
    } catch (error) {
      console.error('FinancialModelStep: Error saving data:', error);
      setError('Failed to save financial model data');
      throw error;
    }
  };

  // Debounced save function for auto-save
  const debouncedSave = useRef(
    debounce(async (dataToSave) => {
      if (!saving && autoSave) {
        try {
          setSaving(true);
          await saveData(dataToSave);
        } catch (error) {
          console.error('FinancialModelStep: Debounced save failed:', error);
        } finally {
          setSaving(false);
        }
      }
    }, 2000)
  ).current;

  // Manual save function for critical changes
  const handleManualSave = async (dataToSave = financialData) => {
    if (readOnly || !onSave || saving) return;
    
    try {
      setSaving(true);
      console.log('FinancialModelStep: Manual save starting...');
      
      // Cancel any pending debounced saves
      debouncedSave.cancel();
      
      // Validate data before saving
      if (!dataToSave || typeof dataToSave !== 'object') {
        throw new Error('Invalid data structure for saving');
      }
      
      // Ensure teams data is properly structured
      if (dataToSave.teams) {
        dataToSave.teams = dataToSave.teams.map((team, index) => {
          if (!team || typeof team !== 'object') {
            console.warn(`FinancialModelStep: Invalid team at index ${index}:`, team);
            return {
              title: 'Unknown Team',
              count: 1,
              avg_salary: 163000,
              time_percentage: 100
            };
          }
          
          return {
            title: String(team.title || 'Unknown Team'),
            count: Math.max(1, Number(team.count) || 1),
            avg_salary: Math.round(Math.max(0, Number(team.avg_salary) || 163000)),
            time_percentage: Math.max(0, Math.min(100, Number(team.time_percentage) || 100))
          };
        });
      }
      
      // Ensure milestones data is properly structured (only efficiency data, not milestone CRUD)
      if (dataToSave.milestones) {
        dataToSave.milestones = dataToSave.milestones.map((milestone, index) => {
          if (!milestone || typeof milestone !== 'object') {
            console.warn(`FinancialModelStep: Invalid milestone at index ${index}:`, milestone);
            return {
              ...milestone,
              efficiency_gains: {},
              efficiency_types: {}
            };
          }
          
          // Validate efficiency data only - don't modify core milestone properties
          const validatedEfficiencyGains = {};
          const validatedEfficiencyTypes = {};
          
          if (milestone.efficiency_gains && typeof milestone.efficiency_gains === 'object') {
            Object.entries(milestone.efficiency_gains).forEach(([teamName, value]) => {
              validatedEfficiencyGains[String(teamName)] = Number(value) || 0;
            });
          }
          
          if (milestone.efficiency_types && typeof milestone.efficiency_types === 'object') {
            Object.entries(milestone.efficiency_types).forEach(([teamName, value]) => {
              const validTypes = Object.values(EFFICIENCY_TYPES);
              const typeValue = String(value);
              validatedEfficiencyTypes[String(teamName)] = validTypes.includes(typeValue) 
                ? typeValue 
                : EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
            });
          }
          
          return {
            ...milestone,
            efficiency_gains: validatedEfficiencyGains,
            efficiency_types: validatedEfficiencyTypes
          };
        });
      }
      
      // Validate core financial fields
      const validatedData = {
        ...dataToSave,
        total_development_cost: Math.round(Math.max(0, Number(dataToSave.total_development_cost) || 0)),
        annual_cola_percentage: Math.max(0, Math.min(20, Number(dataToSave.annual_cola_percentage) || 4.5)),
        projection_years: Math.max(1, Math.min(10, Number(dataToSave.projection_years) || 5)),
        cost_source: dataToSave.cost_source || 'planned',
        efficiency_assumptions: dataToSave.efficiency_assumptions || {
          [EFFICIENCY_TYPES.AUTOMATION_SAVINGS]: 20,
          [EFFICIENCY_TYPES.PROCESS_IMPROVEMENT]: 15,
          [EFFICIENCY_TYPES.ERROR_REDUCTION]: 10,
          [EFFICIENCY_TYPES.TIME_SAVINGS]: 25
        }
      };
      
      console.log('FinancialModelStep: Validated data for save:', JSON.stringify(validatedData, null, 2));
      
      // Trigger immediate save
      await saveData(validatedData);
      console.log('FinancialModelStep: Manual save completed');
    } catch (error) {
      console.error('FinancialModelStep: Manual save failed:', error);
      setError(`Failed to save financial model data: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Function to refresh data from the backend
  const handleRefreshData = useCallback(async () => {
    if (typeof refreshData === 'function' && !refreshing) {
      try {
        setRefreshing(true);
        console.log("FinancialModelStep: Refreshing data from backend...");
        await refreshData();
        console.log("FinancialModelStep: Data refreshed successfully");
      } catch (error) {
        console.error("FinancialModelStep: Error refreshing data:", error);
        setError("Failed to refresh data from the server");
      } finally {
        setRefreshing(false);
      }
    }
  }, [refreshData, refreshing]);

  // Sync with outcome milestones when they change
  useEffect(() => {
    if (data || outcomeData) {
      console.log("FinancialModelStep: Received data:", data);
      console.log("FinancialModelStep: Received outcomeData:", outcomeData);
      
      // ALWAYS prioritize outcomeData milestones and sync with them
      const outcomeMilestones = outcomeData?.milestones || [];
      
      if (outcomeMilestones.length === 0) {
        console.warn("FinancialModelStep: No outcome milestones available");
        return;
      }
      
      console.log("FinancialModelStep: Using outcome milestones:", outcomeMilestones);
      
      // Create updated data structure
      const baseData = {
        ...data,
        teams: data?.teams || [],
        efficiency_assumptions: data?.efficiency_assumptions || {
          [EFFICIENCY_TYPES.AUTOMATION_SAVINGS]: 20,
          [EFFICIENCY_TYPES.PROCESS_IMPROVEMENT]: 15,
          [EFFICIENCY_TYPES.ERROR_REDUCTION]: 10,
          [EFFICIENCY_TYPES.TIME_SAVINGS]: 25
        },
        total_development_cost: Math.round(costEstimationData?.summary?.totalCost || costEstimationData?.totalCost || data?.total_development_cost || 0),
        annual_cola_percentage: data?.annual_cola_percentage || 4.5,
        projection_years: data?.projection_years || 5,
        cost_source: costSource
      };

              // Sync milestones with outcome data (async)
        syncFinancialMilestonesWithOutcome(baseData, outcomeMilestones).then(updatedData => {
          console.log("FinancialModelStep: Updated financial data:", updatedData);
          
          // Force update if milestones are different or other significant changes
          const milestonesChanged = JSON.stringify(outcomeMilestones) !== JSON.stringify(financialData.milestones);
          const significantChanges = hasSignificantChanges(updatedData, financialData);
          
          if (milestonesChanged || significantChanges) {
            console.log("FinancialModelStep: Updating financial data - milestones changed:", milestonesChanged, "other changes:", significantChanges);
            setFinancialData(updatedData);
          }
        }).catch(error => {
          console.error('FinancialModelStep: Error syncing milestones:', error);
        });
      
      // Mark initial load as complete
      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
    }
  }, [data, outcomeData, costEstimationData, costSource, isInitialLoad, syncFinancialMilestonesWithOutcome]);

  // Listen for milestone changes from OutcomeInfoStep
  useEffect(() => {
    const handleMilestoneChange = (event) => {
      if (event.detail.outcomeId === outcomeId) {
        console.log('FinancialModelStep: Outcome milestones changed, syncing...');
        const updatedMilestones = event.detail.milestones;
        
                setFinancialData(prev => {
          syncFinancialMilestonesWithOutcome(prev, updatedMilestones).then(syncedData => {
            setFinancialData(syncedData);
          }).catch(error => {
            console.error('FinancialModelStep: Error syncing milestones from event:', error);
          });
          return prev; // Return current state while async operation completes
        });
      }
    };

    window.addEventListener('outcomeMilestonesChanged', handleMilestoneChange);
    
    return () => {
      window.removeEventListener('outcomeMilestonesChanged', handleMilestoneChange);
    };
  }, [outcomeId, syncFinancialMilestonesWithOutcome]);

    // Update milestone costs when cost source changes
  useEffect(() => {
    if (financialData.milestones && financialData.milestones.length > 0) {
      const updateMilestoneCosts = async () => {
        try {
          const updatedMilestones = await Promise.all(
            financialData.milestones.map(async milestone => ({
              ...milestone,
              cost: await getMilestoneCost(milestone)
            }))
          );
          
          // Only update if costs actually changed
          const costsChanged = updatedMilestones.some((milestone, index) => 
            milestone.cost !== financialData.milestones[index].cost
          );
          
          if (costsChanged || financialData.cost_source !== costSource) {
            const updatedData = {
              ...financialData,
              milestones: updatedMilestones,
              cost_source: costSource
            };
            
            setFinancialData(updatedData);
            
            // Save the cost source change - but only if not initial load and costs actually changed
            if (onSave && !readOnly && !isInitialLoad && costsChanged) {
              handleManualSave(updatedData);
            }
          }
        } catch (error) {
          console.error('FinancialModelStep: Error updating milestone costs:', error);
        }
      };
      
      updateMilestoneCosts();
    }
  }, [costSource, getMilestoneCost, onSave, readOnly, isInitialLoad, financialData.milestones, financialData.cost_source]);

  // Auto-save when financialData changes (only if enabled)
  useEffect(() => {
    if (autoSave && financialData && Object.keys(financialData).length > 0 && !isInitialLoad && !saving) {
      // Only trigger save for significant changes
      if (hasSignificantChanges(financialData, lastSavedData)) {
        debouncedSave(financialData);
      }
    }
  }, [financialData, debouncedSave, autoSave, lastSavedData, isInitialLoad, saving]);

  // Refresh data only when component mounts
  useEffect(() => {
    if (!refreshing && isInitialLoad) {
      handleRefreshData();
    }
  }, []); // Empty dependency array - only run on mount

  // Update cost estimation integration with better change detection
  useEffect(() => {
    if (costEstimationData?.summary?.totalCost || costEstimationData?.totalCost) {
      const newTotalCost = Math.round(costEstimationData.summary?.totalCost || costEstimationData.totalCost || 0);
      
      // Only update if the cost has actually changed to avoid unnecessary re-renders
      if (newTotalCost !== financialData.total_development_cost) {
        console.log('FinancialModelStep: Updating total development cost:', newTotalCost);
        const newData = {
          ...financialData,
          total_development_cost: newTotalCost
        };
        setFinancialData(newData);
        
        // Trigger save for cost updates
        if (onSave && !readOnly && !isInitialLoad) {
          handleManualSave(newData);
        }
      }
    }
  }, [costEstimationData?.summary?.totalCost, costEstimationData?.totalCost, financialData.total_development_cost, isInitialLoad]);

  const calculateTotalCurrentTeamCosts = (fd = financialData) => {
    if (!fd.teams) return 0;
    return Math.round(fd.teams.reduce(
      (sum, team) => sum + (Number(team.count) || 0) * (Number(team.avg_salary) || 0) * (Number(team.time_percentage || 100) / 100),
      0
    ));
  };

  const calculateFutureCosts = (fd = financialData, years = 1) => {
    if (!fd.teams || fd.teams.length === 0) return [];
    const cola = (Number(fd.annual_cola_percentage) || 4.5) / 100;
    return fd.teams.map(team => {
      const currentAnnualCost = Math.round((Number(team.count) || 0) * (Number(team.avg_salary) || 0) * (Number(team.time_percentage || 100) / 100));
      const efficiencyPercentage = calculateTotalEfficiencyForTeam(team.title, fd);
      const yearlyData = Array.from({ length: years }, (_, yearIndex) => {
        const yearNumber = yearIndex + 1;
        const colaMultiplier = Math.pow(1 + cola, yearNumber);
        const adjustedCost = Math.round(currentAnnualCost * colaMultiplier);
        const costAfterEfficiency = Math.round(adjustedCost * (1 - (efficiencyPercentage / 100)));
        const savings = Math.round(adjustedCost - costAfterEfficiency);
        return {
          year: yearNumber,
          currentCost: adjustedCost,
          futureEfficiencyCost: costAfterEfficiency,
          savings: savings
        };
      });
      return {
        team: team.title,
        count: Number(team.count) || 0,
        salary: Math.round(Number(team.avg_salary) || 0),
        timePercentage: Number(team.time_percentage) || 100,
        efficiencyPercentage,
        yearlyData
      };
    });
  };

  const calculateEfficiencyGains = (fd = financialData) => {
    const efficiencyByType = {};
    Object.values(EFFICIENCY_TYPES).forEach(type => {
      efficiencyByType[type] = 0;
    });
    let milestoneEfficiencyGain = 0;
    const teamEfficiencyGains = {};
    const teamEfficiencyByType = {};
    Object.values(EFFICIENCY_TYPES).forEach(type => {
      teamEfficiencyByType[type] = 0;
    });
    (fd.teams || []).forEach(team => {
      let totalGainForTeam = 0;
      const typeGainsForTeam = {};
      Object.values(EFFICIENCY_TYPES).forEach(type => {
        typeGainsForTeam[type] = 0;
      });
      (fd.milestones || []).forEach(milestone => {
        const efficiencyGains = milestone.efficiency_gains || {};
        const efficiencyTypes = milestone.efficiency_types || {};
        const gainForTeam = Number(efficiencyGains[team.title]) || 0;
        const typeForTeam = efficiencyTypes[team.title] || EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
        totalGainForTeam += gainForTeam;
        typeGainsForTeam[typeForTeam] = (typeGainsForTeam[typeForTeam] || 0) + gainForTeam;
      });
      teamEfficiencyGains[team.title] = Math.min(totalGainForTeam, 100);
      const teamCost = Math.round((Number(team.count) || 0) * (Number(team.avg_salary) || 0) * 
                      (Number(team.time_percentage || 100) / 100));
      milestoneEfficiencyGain += Math.round(teamCost * teamEfficiencyGains[team.title] / 100);
      Object.entries(typeGainsForTeam).forEach(([type, gain]) => {
        teamEfficiencyByType[type] += Math.round(teamCost * Math.min(gain, 100) / 100);
        efficiencyByType[type] += Math.round(teamCost * Math.min(gain, 100) / 100);
      });
    });
    const totalEfficiencyGain = Math.round(milestoneEfficiencyGain);
    return {
      total_annual_savings: totalEfficiencyGain,
      team_efficiency_percentages: teamEfficiencyGains,
      milestone_efficiency_gain: milestoneEfficiencyGain,
      efficiency_by_type: teamEfficiencyByType
    };
  };

  const calculateROI = (fd = financialData, years = 1) => {
    const annualSavings = calculateEfficiencyGains(fd).total_annual_savings;
    const investment = Math.round(fd.total_development_cost || 0);
    if (!investment) return 0;
    const cola = (Number(fd.annual_cola_percentage) || 4.5) / 100;
    let totalSavings = 0;
    for (let i = 0; i < years; i++) {
      const yearlyAdjustment = Math.pow(1 + cola, i);
      totalSavings += annualSavings * yearlyAdjustment;
    }
    return ((totalSavings - investment) / investment) * 100;
  };

  const calculatePaybackPeriod = (fd = financialData) => {
    const annualSavings = calculateEfficiencyGains(fd).total_annual_savings;
    const investment = Math.round(fd.total_development_cost || 0);
    if (!annualSavings || annualSavings <= 0) return null;
    return (investment / annualSavings) * 12;
  };

  // Update handleFormDataChange to trigger save
  const handleFormDataChange = async (updates) => {
    const newData = { ...financialData, ...updates };
    setFinancialData(newData);
    
    // Trigger save for form changes
    if (onSave && !readOnly && !isInitialLoad) {
      await handleManualSave(newData);
    }
  };

  // Handle cost source toggle
  const handleCostSourceChange = (event, newCostSource) => {
    if (newCostSource !== null && newCostSource !== costSource) {
      setCostSource(newCostSource);
    }
  };

  // Update save functions to properly save to backend
  const handleSaveTeam = async () => {
    if (readOnly) return;
    
    try {
      console.log('FinancialModelStep: Saving team:', teamForm);
      
      // Validate team form data
      if (!teamForm.title || teamForm.title.trim() === '') {
        setError('Team name is required');
        return;
      }
      
      if (!teamForm.count || teamForm.count < 1) {
        setError('Team size must be at least 1');
        return;
      }
      
      if (!teamForm.avg_salary || teamForm.avg_salary < 0) {
        setError('Average salary must be a positive number');
        return;
      }
      
      if (teamForm.time_percentage < 0 || teamForm.time_percentage > 100) {
        setError('Time percentage must be between 0 and 100');
        return;
      }
      
      const validatedTeamForm = {
        title: String(teamForm.title).trim(),
        count: Math.max(1, Number(teamForm.count)),
        avg_salary: Math.round(Math.max(0, Number(teamForm.avg_salary))),
        time_percentage: Math.max(0, Math.min(100, Number(teamForm.time_percentage)))
      };
      
      const updatedTeams = editingTeam !== null
        ? financialData.teams.map((team, index) =>
            index === editingTeam ? validatedTeamForm : team
          )
        : [...(financialData.teams || []), validatedTeamForm];
      
      const updatedMilestones = JSON.parse(JSON.stringify(financialData.milestones || []));
      
      // Update efficiency data for each milestone
      updatedMilestones.forEach(milestone => {
        if (!milestone.efficiency_gains) milestone.efficiency_gains = {};
        if (!milestone.efficiency_types) milestone.efficiency_types = {};
        
        if (editingTeam === null) {
          // Adding new team
          milestone.efficiency_gains[validatedTeamForm.title] = 0;
          milestone.efficiency_types[validatedTeamForm.title] = EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
        } else if (validatedTeamForm.title !== financialData.teams[editingTeam].title) {
          // Renaming existing team
          const oldTitle = financialData.teams[editingTeam].title;
          milestone.efficiency_gains[validatedTeamForm.title] = 
            milestone.efficiency_gains[oldTitle] || 0;
          milestone.efficiency_types[validatedTeamForm.title] = 
            milestone.efficiency_types[oldTitle] || EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
          delete milestone.efficiency_gains[oldTitle];
          delete milestone.efficiency_types[oldTitle];
        }
      });
      
      const newData = {
        ...financialData,
        teams: updatedTeams,
        milestones: updatedMilestones
      };
      
      setFinancialData(newData);
      
      // Save to backend
      await handleManualSave(newData);
      
      console.log('FinancialModelStep: Team saved successfully');
      setTeamDialogOpen(false);
      setEditingTeam(null);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("FinancialModelStep: Error saving team data:", error);
      setError(`Failed to save team data: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteTeam = async (index) => {
    if (readOnly) return;
    
    try {
      const teamTitle = financialData.teams[index].title;
      
      // Create deep copies to avoid mutation issues
      const updatedTeams = financialData.teams.filter((_, i) => i !== index);
      const updatedMilestones = JSON.parse(JSON.stringify(financialData.milestones));
      
      // Remove efficiency data for deleted team
      updatedMilestones.forEach(milestone => {
        if (milestone.efficiency_gains) {
          delete milestone.efficiency_gains[teamTitle];
        }
        if (milestone.efficiency_types) {
          delete milestone.efficiency_types[teamTitle];
        }
      });
      
      // Update local state
      const newData = {
        ...financialData,
        teams: updatedTeams,
        milestones: updatedMilestones
      };
      
      setFinancialData(newData);
      
      // Save to backend
      await handleManualSave(newData);
      
      console.log('FinancialModelStep: Team deleted successfully');
    } catch (error) {
      console.error("FinancialModelStep: Error deleting team:", error);
      setError("Failed to delete team. Please try again.");
    }
  };

  const handleEditTeam = (index) => {
    setEditingTeam(index);
    setTeamForm({
      ...financialData.teams[index],
      avg_salary: Math.round(financialData.teams[index].avg_salary)
    });
    setTeamDialogOpen(true);
  };

  const handleAddTeam = () => {
    setEditingTeam(null);
    setTeamForm({
      title: '',
      count: 1,
      avg_salary: DEFAULT_CA_SALARY,
      time_percentage: 100
    });
    setTeamDialogOpen(true);
  };

  const handleTeamFormChange = (field, value) => {
    if (field === 'avg_salary') {
      value = Math.round(value);
    }
    setTeamForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOpenEfficiencyDialog = (milestoneIndex, teamTitle) => {
    console.log('FinancialModelStep: Opening efficiency dialog for milestone:', milestoneIndex, 'team:', teamTitle);
    console.log('FinancialModelStep: Current milestone data:', financialData.milestones[milestoneIndex]);
    
    setSelectedMilestone(milestoneIndex);
    setSelectedTeam(teamTitle);
    
    const milestone = financialData.milestones[milestoneIndex];
    const efficiencyGains = milestone.efficiency_gains || {};
    const efficiencyTypes = milestone.efficiency_types || {};
    
    const currentEfficiencyValue = Number(efficiencyGains[teamTitle]) || 0;
    const currentEfficiencyType = String(efficiencyTypes[teamTitle]) || EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
    
    console.log('FinancialModelStep: Setting efficiency dialog values:', {
      value: currentEfficiencyValue,
      type: currentEfficiencyType
    });
    
    setEfficiencyValue(currentEfficiencyValue);
    setSelectedEfficiencyType(currentEfficiencyType);
    setEfficiencyDialogOpen(true);
  };

  const handleSaveEfficiency = async () => {
    if (readOnly) return;
    
    try {
      setSavingEfficiency(true);
      setEfficiencySaveSuccess(false);
      setError(null);
      
      console.log('FinancialModelStep: Saving efficiency for team:', selectedTeam, 'value:', efficiencyValue, 'type:', selectedEfficiencyType);
      
      // Create a deep copy of the financial data to avoid mutation issues
      const updatedFinancialData = JSON.parse(JSON.stringify(financialData));
      
      // Ensure the milestone exists and has the required structure
      if (!updatedFinancialData.milestones[selectedMilestone]) {
        console.error('FinancialModelStep: Selected milestone does not exist');
        setError('Selected milestone not found');
        return;
      }
      
      // Ensure the efficiency_gains and efficiency_types objects exist
      if (!updatedFinancialData.milestones[selectedMilestone].efficiency_gains) {
        updatedFinancialData.milestones[selectedMilestone].efficiency_gains = {};
      }
      if (!updatedFinancialData.milestones[selectedMilestone].efficiency_types) {
        updatedFinancialData.milestones[selectedMilestone].efficiency_types = {};
      }
      
      // Set the efficiency values with proper type conversion
      updatedFinancialData.milestones[selectedMilestone].efficiency_gains[selectedTeam] = Number(efficiencyValue);
      updatedFinancialData.milestones[selectedMilestone].efficiency_types[selectedTeam] = String(selectedEfficiencyType);
      
      console.log("FinancialModelStep: Updated milestone:", updatedFinancialData.milestones[selectedMilestone]);
      
      // Update local state immediately
      setFinancialData(updatedFinancialData);
      
      // Save to backend with explicit data validation - DON'T refresh after save
      console.log("FinancialModelStep: Saving efficiency data to backend");
      await saveData(updatedFinancialData); // Use saveData directly to avoid refresh
      console.log("FinancialModelStep: Efficiency data saved to backend successfully");
      
      // Show success feedback
      setEfficiencySaveSuccess(true);
      
      // Close the dialog after a short delay to show success feedback
      setTimeout(() => {
        setEfficiencyDialogOpen(false);
        setEfficiencySaveSuccess(false);
      }, 1000);
    } catch (error) {
      console.error("FinancialModelStep: Error saving efficiency data:", error);
      setError(`Failed to save efficiency data: ${error.message || 'Unknown error'}`);
    } finally {
      setSavingEfficiency(false);
    }
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    
    // Teams sheet
    if (financialData.teams && financialData.teams.length > 0) {
      const teamsSheet = XLSX.utils.json_to_sheet(financialData.teams.map(team => ({
        ...team,
        avg_salary: Math.round(team.avg_salary)
      })));
      XLSX.utils.book_append_sheet(wb, teamsSheet, 'Current Process Teams');
    }
    
    // Milestones sheet
    if (financialData.milestones && financialData.milestones.length > 0) {
      // Flatten milestone data for Excel
      const flattenedMilestones = financialData.milestones.map(milestone => {
        const flat = { 
          ...milestone,
          cost: Math.round(milestone.cost),
          cost_source: costSource
        };
        delete flat.efficiency_gains;
        delete flat.efficiency_types;
        
        // Add efficiency gains as separate columns
        financialData.teams.forEach(team => {
          flat[`${team.title} Efficiency`] = milestone.efficiency_gains?.[team.title] || 0;
          flat[`${team.title} Type`] = milestone.efficiency_types?.[team.title] || EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
        });
        
        return flat;
      });
      
      const milestonesSheet = XLSX.utils.json_to_sheet(flattenedMilestones);
      XLSX.utils.book_append_sheet(wb, milestonesSheet, 'Milestones');
    }
    
    // Cost projections sheet
    const futureCosts = calculateFutureCosts(financialData, financialData.projection_years || 5);
    const costProjections = [];
    
    futureCosts.forEach(teamCost => {
      teamCost.yearlyData.forEach(yearData => {
        costProjections.push({
          Team: teamCost.team,
          Year: yearData.year,
          'Current Cost': Math.round(yearData.currentCost),
          'Future Cost': Math.round(yearData.futureEfficiencyCost),
          'Annual Savings': Math.round(yearData.savings),
          'Efficiency %': teamCost.efficiencyPercentage
        });
      });
    });
    
    const costProjectionsSheet = XLSX.utils.json_to_sheet(costProjections);
    XLSX.utils.book_append_sheet(wb, costProjectionsSheet, 'Cost Projections');
    
    // Summary sheet
    const efficiencyGains = calculateEfficiencyGains();
    const summary = [
      {
        cost_source: costSource,
        total_investment: Math.round(financialData.total_development_cost),
        total_current_team_costs: calculateTotalCurrentTeamCosts(),
        annual_efficiency_gains: Math.round(efficiencyGains.total_annual_savings),
        roi_1yr: calculateROI(financialData, 1),
        roi_5yr: calculateROI(financialData, 5),
        payback_period_months: calculatePaybackPeriod(),
        annual_cola_percentage: financialData.annual_cola_percentage,
        ...Object.values(EFFICIENCY_TYPES).reduce((acc, type) => {
          acc[`${type} Savings`] = Math.round(efficiencyGains.efficiency_by_type?.[type] || 0);
          return acc;
        }, {})
      }
    ];
    const summarySheet = XLSX.utils.json_to_sheet(summary);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
    
    XLSX.writeFile(wb, `FinancialModel_${outcomeName || 'Outcome'}_${costSource}.xlsx`);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const calculateTotalEfficiencyForTeam = (teamTitle, fd = financialData) => {
    let total = 0;
    (fd.milestones || []).forEach(milestone => {
      const efficiencyGains = milestone.efficiency_gains || {};
      total += Number(efficiencyGains[teamTitle]) || 0;
    });
    return Math.min(total, 100);
  };

  const calculateTotalEfficiencyForTeams = (fd = financialData) => {
    const totals = {};
    (fd.teams || []).forEach(team => {
      totals[team.title] = calculateTotalEfficiencyForTeam(team.title, fd);
    });
    return totals;
  };

  // Add this function to help debug the data structure
  const logMilestoneData = () => {
    console.log("FinancialModelStep: Current milestone data:", financialData.milestones.map(m => ({
      name: m.name,
      cost: Math.round(m.cost),
      cost_source: costSource,
      efficiency_gains: m.efficiency_gains,
      efficiency_types: m.efficiency_types
    })));
  };

  const teamEfficiencyTotals = calculateTotalEfficiencyForTeams();
  const efficiencyGains = calculateEfficiencyGains();
  const futureCosts = calculateFutureCosts(financialData, financialData.projection_years || 5);
  const paybackPeriod = calculatePaybackPeriod();

  const calculateTotalSavingsByYear = (years = 5) => {
    const yearlyTotals = Array(years).fill(0);
    futureCosts.forEach(teamCost => {
      if (teamCost && teamCost.yearlyData) {
        teamCost.yearlyData.forEach((yearData, index) => {
          if (index < years && yearData) {
            yearlyTotals[index] += Math.round(yearData.savings || 0);
          }
        });
      }
    });
    return yearlyTotals;
  };

  const totalSavingsByYear = calculateTotalSavingsByYear(financialData.projection_years || 5);

  const chartData = React.useMemo(() => {
    try {
      return prepareStackedBarChartData(
        futureCosts, 
        financialData.total_development_cost || 0,
        financialData.projection_years || 5,
        paybackPeriod
      );
    } catch (error) {
      console.error('FinancialModelStep: Error preparing chart data:', error);
      return [];
    }
  }, [futureCosts, financialData.total_development_cost, financialData.projection_years, paybackPeriod]);

  const safeChartData = Array.isArray(chartData) ? chartData : [];

  // Add save button for manual control when auto-save is disabled
  const renderSaveButton = () => {
    if (readOnly || autoSave) return null;
    
    return (
      <Button
        variant="outlined"
        onClick={() => handleManualSave()}
        disabled={saving}
        startIcon={saving ? <CircularProgress size={16} /> : null}
        sx={{ mr: 1 }}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">
          Step 3 of 5: Financial Model
        </Typography>
        {outcomeName && (
          <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
            {outcomeName}
          </Typography>
        )}
        {saving && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Saving changes...
            </Typography>
          </Box>
        )}
        {!autoSave && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Auto-save is disabled. Use the Save button to save your changes.
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Cost Source Toggle */}
      <Card elevation={1} sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
                Cost Source
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Choose between planned costs from estimation or actual staffed costs
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={costSource}
              exclusive
              onChange={handleCostSourceChange}
              size="small"
              disabled={readOnly}
            >
              <ToggleButton value="planned" sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon fontSize="small" />
                  <Typography variant="body2">Planned Costs</Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="staffed" sx={{ px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssessmentIcon fontSize="small" />
                  <Typography variant="body2">Actual Staffed Costs</Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {/* Cost source explanation */}
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              {costSource === 'planned' ? (
                <>
                  <strong>Planned Costs:</strong> Uses cost estimation data with role allocations and timeline projections.
                  This represents the initial project budget and resource planning.
                </>
              ) : (
                <>
                  <strong>Actual Staffed Costs:</strong> Uses actual staffing assignments with specific team members and their allocations.
                  This represents the real costs based on who is actually assigned to work on the project.
                </>
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Involved Teams" />
          <Tab label="Milestones" />
          <Tab label="Summary" />
          <Tab label="Charts" />
        </Tabs>
        
        <Button
          variant="outlined"
          startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
          onClick={handleRefreshData}
          disabled={refreshing}
          size="small"
          sx={secondaryButtonStyle}
        >
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </Button>
      </Box>

      {activeTab === 0 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardHeader
            title="Involved Teams"
            subheader="Teams currently involved in the process that would benefit from this outcome"
            action={
              !readOnly && (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddTeam}
                  size="small"
                  sx={localButtonStyle}
                >
                  Add Team
                </Button>
              )
            }
          />
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Team Name</TableCell>
                    <TableCell>Team Size</TableCell>
                    <TableCell>Avg. Salary per Member</TableCell>
                    <TableCell>% Time on Process</TableCell>
                    <TableCell>Total Annual Cost</TableCell>
                    <TableCell>Total Efficiency Gain</TableCell>
                    {!readOnly && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(financialData.teams || []).map((team, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{team.title}</TableCell>
                      <TableCell>{team.count}</TableCell>
                      <TableCell>${Math.round(team.avg_salary).toLocaleString()}</TableCell>
                      <TableCell>{team.time_percentage}%</TableCell>
                      <TableCell>
                        ${Math.round(Number(team.count) * Number(team.avg_salary) * 
                          (Number(team.time_percentage || 100) / 100)).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            sx={(theme) => ({
                              color: teamEfficiencyTotals[team.title] > 0 
                                ? theme.palette.mode === 'dark' 
                                  ? theme.palette.success.light 
                                  : theme.palette.success.main 
                                : theme.palette.text.secondary
                            })}
                          >
                            {teamEfficiencyTotals[team.title] || 0}%
                          </Typography>
                          <Tooltip title="Total efficiency gain from all milestones">
                            <InfoIcon fontSize="small" sx={{ ml: 1, color: 'action.active' }} />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {!readOnly && (
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditTeam(idx)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleDeleteTeam(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {(!financialData.teams || financialData.teams.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={readOnly ? 6 : 7} align="center">
                        No teams added yet. Add teams that are currently involved in the process.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                <strong>Note:</strong> These are the teams currently doing the work that this outcome will improve. 
                This is different from the development team building the solution. Add teams like "Customer Service", 
                "Data Entry", "Quality Assurance", etc. that spend time on the current process.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">Project Milestones</Typography>
                <Chip
                  label={`Cost (${costSource})`}
                  size="small"
                  color={costSource === 'staffed' ? 'primary' : 'secondary'}
                  variant="outlined"
                />
              </Box>
            }
            subheader="Milestones are inherited from Outcome Information. Set efficiency gains for each team per milestone."
            action={
              <IconButton 
                size="small" 
                onClick={() => setShowDebug(!showDebug)}
                title="Toggle debug view"
              >
                <InfoIcon />
              </IconButton>
            }
          />
          <CardContent>
            {showDebug && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, overflow: 'auto', maxHeight: 200 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Debug: Efficiency Data</Typography>
                  <Button 
                    size="small" 
                    variant="outlined" 
                    onClick={logMilestoneData}
                  >
                    Log Data
                  </Button>
                </Box>
                <pre style={{ fontSize: '0.75rem' }}>
                  {JSON.stringify(financialData.milestones?.map(m => ({
                    name: m.name,
                    cost: Math.round(m.cost),
                    cost_source: costSource,
                    efficiency_gains: m.efficiency_gains,
                    efficiency_types: m.efficiency_types
                  })), null, 2)}
                </pre>
              </Box>
            )}
            
            {/* Cost source info */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Current Cost Source:</strong> {costSource === 'planned' ? 'Planned Costs' : 'Actual Staffed Costs'}
                {costSource === 'planned' && costEstimationData ? 
                  ' - Costs calculated from cost estimation roles and timeline allocations.' :
                  costSource === 'staffed' && staffingData ? 
                  ' - Costs calculated from actual staffing assignments and member allocations.' :
                  ' - No data available for selected cost source.'
                }
              </Typography>
            </Alert>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Cost ({costSource})</TableCell>
                    <TableCell>Timeline</TableCell>
                    <TableCell>Efficiency Gains</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(financialData.milestones || []).map((milestone, idx) => (
                    <TableRow key={milestone.id || idx}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {milestone.name}
                          </Typography>
                          {milestone.capex_classification && (
                            <Chip
                              label={milestone.capex_classification}
                              size="small"
                              color={milestone.capex_classification === 'CAPEX' ? 'primary' : 'success'}
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 18, mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          ${Math.round(milestone.cost || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {costSource === 'planned' ? 'from estimation' : 'from staffing'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {milestone.start_date ? dayjs(milestone.start_date).format('MMM DD, YYYY') : '-'} - {' '}
                          {milestone.end_date ? dayjs(milestone.end_date).format('MMM DD, YYYY') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {financialData.teams && financialData.teams.map((team, teamIdx) => {
                            // Ensure efficiency_gains and efficiency_types exist
                            const efficiencyGains = milestone.efficiency_gains || {};
                            const efficiencyTypes = milestone.efficiency_types || {};
                            
                            const efficiencyValue = Number(efficiencyGains[team.title]) || 0;
                            const efficiencyType = String(efficiencyTypes[team.title]) || EFFICIENCY_TYPES.PROCESS_IMPROVEMENT;
                            
                            return (
                              <Box 
                                key={teamIdx} 
                                sx={(theme) => ({ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'space-between',
                                  bgcolor: efficiencyValue > 0 
                                    ? theme.palette.mode === 'dark' 
                                      ? alpha(theme.palette.success.main, 0.15) 
                                      : alpha(theme.palette.success.light, 0.3)
                                    : theme.palette.mode === 'dark'
                                      ? alpha(theme.palette.action.hover, 0.1)
                                      : theme.palette.action.hover,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1
                                })}
                              >
                                <Typography variant="caption">{team.title}:</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      fontWeight: efficiencyValue > 0 ? 'bold' : 'normal',
                                      color: theme => efficiencyValue > 0 
                                        ? theme.palette.mode === 'dark' 
                                          ? theme.palette.success.light 
                                          : theme.palette.success.dark
                                        : 'inherit'
                                    }}
                                  >
                                    {efficiencyValue}% ({efficiencyType})
                                  </Typography>
                                  {!readOnly && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleOpenEfficiencyDialog(idx, team.title)}
                                      sx={{ ml: 1 }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!financialData.milestones || financialData.milestones.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No milestones available. Please add milestones in the Outcome Information step.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Data availability indicators */}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Chip
                label={`Cost Estimation: ${costEstimationData?.roles ? 'Available' : 'Not Available'}`}
                size="small"
                color={costEstimationData?.roles ? 'success' : 'default'}
                variant="outlined"
              />
              <Chip
                label={`Staffing Data: ${staffingData?.milestones ? 'Available' : 'Not Available'}`}
                size="small"
                color={staffingData?.milestones ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <>
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardHeader
              title="Financial Summary"
              subheader={`Overview of costs, efficiency gains, and ROI using ${costSource} costs`}
            />
            <CardContent>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cost of Living Adjustment (COLA)
                    </Typography>
                    <TextField
                      label="Annual COLA Percentage"
                      type="number"
                      value={financialData.annual_cola_percentage}
                      onChange={(e) => handleFormDataChange({
                        annual_cola_percentage: Number(e.target.value)
                      })}
                      InputProps={{
                        endAdornment: <Typography variant="body2">%</Typography>,
                        readOnly: readOnly
                      }}
                      inputProps={{ min: 0, max: 20, step: 0.1 }}
                      fullWidth
                      sx={{ mb: 2 }}
                      helperText="Annual salary increase percentage for cost projections"
                      disabled={readOnly}
                    />
                    
                    <TextField
                      label="Projection Years"
                      type="number"
                      value={financialData.projection_years}
                      onChange={(e) => handleFormDataChange({
                        projection_years: Number(e.target.value)
                      })}
                      inputProps={{ min: 1, max: 10, step: 1 }}
                      fullWidth
                      helperText="Number of years to project costs and savings"
                      disabled={readOnly}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SummaryCards
                    showFinancial={true}
                    financialData={financialData}
                    calculateEfficiencyGains={calculateEfficiencyGains}
                    calculateROI={calculateROI}
                    calculatePaybackPeriod={calculatePaybackPeriod}
                  />
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      ROI Projections
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Card variant="outlined" sx={(theme) => ({ 
                          p: 2, 
                          bgcolor: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.info.main, 0.15) 
                            : alpha(theme.palette.info.light, 0.3),
                          color: theme.palette.mode === 'dark'
                            ? theme.palette.info.light
                            : theme.palette.info.dark
                        })}>
                          <Typography variant="subtitle2">1-Year ROI</Typography>
                          <Typography variant="h5">
                            {calculateROI(financialData, 1).toFixed(1)}%
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={6}>
                        <Card variant="outlined" sx={(theme) => ({ 
                          p: 2, 
                          bgcolor: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.success.main, 0.15) 
                            : alpha(theme.palette.success.light, 0.3),
                          color: theme.palette.mode === 'dark'
                            ? theme.palette.success.light
                            : theme.palette.success.dark
                        })}>
                          <Typography variant="subtitle2">5-Year ROI</Typography>
                          <Typography variant="h5">
                            {calculateROI(financialData, 5).toFixed(1)}%
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Payback Period
                    </Typography>
                    <Card variant="outlined" sx={(theme) => ({ 
                      p: 2, 
                      bgcolor: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.warning.main, 0.15) 
                        : alpha(theme.palette.warning.light, 0.3),
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.warning.light
                        : theme.palette.warning.dark
                    })}>
                      <Typography variant="subtitle2">Months to Break Even</Typography>
                      <Typography variant="h5">
                        {calculatePaybackPeriod() ? calculatePaybackPeriod().toFixed(1) + ' months' : 'N/A'}
                      </Typography>
                    </Card>
                  </Box>
                  
                  <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                    Efficiency Gains by Type
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Efficiency Type</TableCell>
                          <TableCell align="right">Annual Savings</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.values(EFFICIENCY_TYPES).map(type => (
                          <TableRow key={type}>
                            <TableCell>{type}</TableCell>
                            <TableCell align="right">
                              ${Math.round(efficiencyGains.efficiency_by_type[type] || 0).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={(theme) => ({ 
                          bgcolor: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.success.main, 0.15) 
                            : alpha(theme.palette.success.light, 0.3)
                        })}>
                          <TableCell><strong>Total Efficiency Gains</strong></TableCell>
                          <TableCell align="right">
                            <strong>${Math.round(efficiencyGains.total_annual_savings).toLocaleString()}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          <Card elevation={2} sx={{ mb: 3 }}>
            <CardHeader
              title="Team Cost Projections"
              subheader={`Current vs. Future Costs with ${financialData.annual_cola_percentage}% Annual COLA`}
            />
            <CardContent>
              {futureCosts && futureCosts.length > 0 ? (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Team</TableCell>
                          <TableCell>Size</TableCell>
                          <TableCell>Efficiency</TableCell>
                          <TableCell>Year 1 Current</TableCell>
                          <TableCell>Year 1 Future</TableCell>
                          <TableCell>Year 1 Savings</TableCell>
                          <TableCell>Year 5 Current</TableCell>
                          <TableCell>Year 5 Future</TableCell>
                          <TableCell>Year 5 Savings</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {futureCosts.map((teamCost, idx) => {
                          const year1 = teamCost.yearlyData[0] || { currentCost: 0, futureEfficiencyCost: 0, savings: 0 };
                          const year5 = teamCost.yearlyData[4] || teamCost.yearlyData[teamCost.yearlyData.length - 1] || year1;
                          
                          return (
                            <TableRow key={idx}>
                              <TableCell>{teamCost.team}</TableCell>
                              <TableCell>{teamCost.count}</TableCell>
                              <TableCell>{teamCost.efficiencyPercentage}%</TableCell>
                              <TableCell>${Math.round(year1.currentCost).toLocaleString()}</TableCell>
                              <TableCell>${Math.round(year1.futureEfficiencyCost).toLocaleString()}</TableCell>
                              <TableCell sx={(theme) => ({ 
                                color: theme.palette.mode === 'dark' 
                                  ? theme.palette.success.light 
                                  : theme.palette.success.main 
                              })}>
                                ${Math.round(year1.savings).toLocaleString()}
                              </TableCell>
                              <TableCell>${Math.round(year5.currentCost).toLocaleString()}</TableCell>
                              <TableCell>${Math.round(year5.futureEfficiencyCost).toLocaleString()}</TableCell>
                              <TableCell sx={(theme) => ({ 
                                color: theme.palette.mode === 'dark' 
                                  ? theme.palette.success.light 
                                  : theme.palette.success.main 
                              })}>
                                ${Math.round(year5.savings).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        
                        {/* Totals row */}
                        <TableRow sx={(theme) => ({ 
                          bgcolor: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.success.main, 0.15) 
                            : alpha(theme.palette.success.light, 0.3)
                        })}>
                          <TableCell colSpan={5}><strong>Total Annual Savings</strong></TableCell>
                          <TableCell>
                            <strong>${Math.round(totalSavingsByYear[0] || 0).toLocaleString()}</strong>
                          </TableCell>
                          <TableCell colSpan={2}></TableCell>
                          <TableCell>
                            <strong>${Math.round(totalSavingsByYear[4] || totalSavingsByYear[totalSavingsByYear.length - 1] || 0).toLocaleString()}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cumulative Savings Over {financialData.projection_years} Years
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {Array.from({ length: financialData.projection_years }, (_, i) => (
                              <TableCell key={i} align="center">Year {i + 1}</TableCell>
                            ))}
                            <TableCell align="center">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            {totalSavingsByYear.map((savings, idx) => (
                              <TableCell key={idx} align="center">
                                ${Math.round(savings).toLocaleString()}
                              </TableCell>
                            ))}
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                              ${Math.round(totalSavingsByYear.reduce((sum, val) => sum + val, 0)).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Investment vs. Cumulative Savings
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Initial Investment ({costSource})</TableCell>
                            <TableCell>Year 1 Savings</TableCell>
                            <TableCell>Year 3 Cumulative</TableCell>
                            <TableCell>Year 5 Cumulative</TableCell>
                            <TableCell>Break-even (Months)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>${Math.round(financialData.total_development_cost).toLocaleString()}</TableCell>
                            <TableCell>${Math.round(totalSavingsByYear[0] || 0).toLocaleString()}</TableCell>
                            <TableCell>
                              ${Math.round(
                                totalSavingsByYear.slice(0, 3).reduce((sum, val) => sum + val, 0)
                              ).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              ${Math.round(
                                totalSavingsByYear.slice(0, 5).reduce((sum, val) => sum + val, 0)
                              ).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {calculatePaybackPeriod() ? calculatePaybackPeriod().toFixed(1) + ' months' : 'N/A'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Add teams to see cost projections
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Charts Tab */}
      {activeTab === 3 && (
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardHeader
            title="Financial Charts"
            subheader={`Visualizations of costs, savings, and ROI using ${costSource} costs`}
          />
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Development Cost vs. Team Savings by Year
            </Typography>
            
            {safeChartData && safeChartData.length > 0 ? (
              <Box sx={{ height: 500, width: '100%' }}>
                {/* Custom stacked bar chart implementation */}
                <Box sx={{ height: '100%', width: '100%', p: 2 }}>
                  {(() => {
                    try {
                      // Extract team names for the legend
                      const teamNames = [];
                      if (futureCosts && futureCosts.length > 0) {
                        futureCosts.forEach(teamCost => {
                          teamNames.push(teamCost.team);
                        });
                      }
                      
                      // Generate colors for the bars
                      const colors = ['#ff7300', '#82ca9d', '#ffc658', '#8884d8', '#0088fe', '#00C49F', '#FFBB28', '#FF8042'];
                      
                      return (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          {/* Legend */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, justifyContent: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: colors[0], borderRadius: 0.5 }} />
                              <Typography variant="body2">Development Cost (Negative)</Typography>
                            </Box>
                            
                            {teamNames.map((team, idx) => (
                              <Box key={team} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 16, height: 16, bgcolor: colors[idx + 1], borderRadius: 0.5 }} />
                                <Typography variant="body2">{team} Savings</Typography>
                              </Box>
                            ))}
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, border: '2px solid red', borderRadius: '50%' }} />
                              <Typography variant="body2">Net Value</Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 16, height: 16, bgcolor: 'orange', borderRadius: '50%' }} />
                              <Typography variant="body2">Break-even Point</Typography>
                            </Box>
                          </Box>
                          
                          {/* Chart visualization would go here - simplified for this example */}
                          <Box sx={{ 
                            flex: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            bgcolor: 'background.paper'
                          }}>
                            <Typography variant="body2" color="text.secondary">
                              Chart visualization area - {safeChartData.length} years of data using {costSource} costs
                            </Typography>
                          </Box>
                        </Box>
                      );
                    } catch (error) {
                      console.error("FinancialModelStep: Error rendering custom chart:", error);
                      return (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%' 
                        }}>
                          <Typography variant="body1" color="error" gutterBottom>
                            Error rendering chart visualization
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Please check the console for details
                          </Typography>
                        </Box>
                      );
                    }
                  })()}
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: 300 
              }}>
                <Typography variant="body1" color="text.secondary">
                  Add teams to see cost projections
                </Typography>
              </Box>
            )}
            
            {/* Data table below the chart */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle1" gutterBottom>
                Year-by-Year Data ({costSource} costs)
              </Typography>
              {safeChartData && safeChartData.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Year</TableCell>
                        <TableCell>Development Cost</TableCell>
                        {futureCosts && futureCosts.map(teamCost => (
                          <TableCell key={teamCost.team}>{teamCost.team} Savings</TableCell>
                        ))}
                        <TableCell>Net Value</TableCell>
                        <TableCell>Break-even</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {safeChartData.map((yearData, idx) => (
                        <TableRow 
                          key={idx}
                          sx={yearData.isBreakEven ? {
                            bgcolor: theme => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.1)
                          } : {}}
                        >
                          <TableCell>{yearData.year}</TableCell>
                          <TableCell sx={{ color: yearData.devCost < 0 ? 'error.main' : 'inherit' }}>
                            ${yearData.devCost < 0 ? '-' : ''}${Math.abs(Math.round(yearData.devCost || 0)).toLocaleString()}
                          </TableCell>
                          {futureCosts && futureCosts.map(teamCost => (
                            <TableCell key={teamCost.team} sx={{ color: 'success.main' }}>
                              ${Math.round(yearData[`${teamCost.team} Savings`] || 0).toLocaleString()}
                            </TableCell>
                          ))}
                          <TableCell sx={{ 
                            fontWeight: 'bold',
                            color: yearData.netValue >= 0 ? 'success.main' : 'error.main'
                          }}>
                            ${yearData.netValue >= 0 ? '' : '-'}${Math.abs(Math.round(yearData.netValue || 0)).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            {yearData.isBreakEven && (
                              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                                Yes
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No data available
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Navigation buttons - only show if hideButtons is false */}
      {!hideButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            variant="outlined"
            size="large"
            sx={{ minWidth: 120, ...(secondaryButtonStyle || {}) }}
            disabled
          >
            Previous
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleExportExcel}
              size="large"
              startIcon={<ExportIcon />}
              sx={{ minWidth: 140, ...(secondaryButtonStyle || {}) }}
            >
              Export Excel
            </Button>
            {renderSaveButton()}
            <Button
              variant="contained"
              size="large"
              sx={{ minWidth: 150, ...(localButtonStyle || {}) }}
              disabled={saving}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}

      {/* Team Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editingTeam !== null ? 'Edit Team' : 'Add Team'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Team Name"
            value={teamForm.title}
            onChange={e => handleTeamFormChange('title', e.target.value)}
            fullWidth
            sx={{ mb: 2, mt: 1 }}
            placeholder="e.g., Customer Service Team, Data Entry Team"
          />
          <TextField
            label="Team Size"
            type="number"
            value={teamForm.count}
            onChange={e => handleTeamFormChange('count', e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Number of people on this team"
          />
          <TextField
            label="Avg. Salary per Team Member"
            type="number"
            value={teamForm.avg_salary}
            onChange={e => handleTeamFormChange('avg_salary', Math.round(Number(e.target.value)))}
            fullWidth
            sx={{ mb: 2 }}
            helperText="Annual salary per team member (CA default: $163,000)"
          />
          <TextField
            label="% of Time Spent on Current Process"
            type="number"
            value={teamForm.time_percentage}
            onChange={e => handleTeamFormChange('time_percentage', 
              Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
            fullWidth
            InputProps={{
              endAdornment: <Typography variant="body2">%</Typography>,
            }}
            inputProps={{ min: 0, max: 100 }}
            helperText="Percentage of time this team spends on the current process (0-100%)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTeam} variant="contained" sx={localButtonStyle}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Efficiency Dialog */}
      <Dialog 
        open={efficiencyDialogOpen} 
        onClose={() => setEfficiencyDialogOpen(false)} 
        maxWidth="xs" 
        fullWidth
        disablePortal={false}
      >
        <DialogTitle>
          Set Efficiency Gain
          {selectedTeam && (
            <Typography variant="subtitle2" color="text.secondary">
              Team: {selectedTeam}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Efficiency Gain (%)
            </Typography>
            <Slider
              value={Number(efficiencyValue)}
              onChange={(e, value) => setEfficiencyValue(Number(value))}
              min={0}
              max={50}
              step={1}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' }
              ]}
              valueLabelDisplay="on"
            />
            
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Efficiency Type</InputLabel>
              <Select
                value={String(selectedEfficiencyType)}
                label="Efficiency Type"
                onChange={e => setSelectedEfficiencyType(String(e.target.value))}
              >
                {Object.values(EFFICIENCY_TYPES).map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEfficiencyDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveEfficiency} 
            variant="contained" 
            sx={localButtonStyle}
            disabled={savingEfficiency}
            startIcon={savingEfficiency ? <CircularProgress size={20} /> : null}
          >
            {savingEfficiency ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
        
        {efficiencySaveSuccess && (
          <Alert severity="success" sx={{ m: 2 }}>
            Efficiency data saved successfully!
          </Alert>
        )}
      </Dialog>
    </Box>
  );
}

export default FinancialModelStep;